import React, { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldOff, Cpu, Film, Ghost } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const EXEC_STYLE = {
  GENUINE_LOCAL_INFERENCE: { cls: "bg-emerald-100 text-emerald-800", icon: Cpu, short: "Local AI inference" },
  DETERMINISTIC_COMPOSITE: { cls: "bg-sky-100 text-sky-800", icon: Film, short: "FFmpeg composite · not AI video" },
  MOCK_OUTPUT: { cls: "bg-amber-100 text-amber-800", icon: Ghost, short: "Mock output · no AI" },
  EXTERNAL_ADAPTER_DISABLED: { cls: "bg-rose-100 text-rose-800", icon: ShieldOff, short: "External · disabled" },
};
const errMsg = (e) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || "Request failed"; };

function ExecBadge({ t, testid }) {
  const s = EXEC_STYLE[t];
  if (!s) return <span className="text-[10px] text-stone-400">pending</span>;
  const I = s.icon;
  return <span data-testid={testid} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${s.cls}`}><I className="w-3 h-3" />{s.short}</span>;
}

function EngineList({ reg, health }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2" data-testid="p11-engines">
      {reg.engines.map((e) => {
        const h = health?.engines?.[e.engine_id] || {};
        const state = e.type === "external_adapter" ? "DISABLED" : h.ready ? "READY" : "BLOCKED";
        return (
          <div key={e.engine_id} className="rounded-xl border border-stone-200 p-2" data-testid={`p11-engine-${e.engine_id}`}>
            <div className="flex items-center gap-2"><b className="text-xs">{e.name}</b>
              <span data-testid={`p11-engine-state-${e.engine_id}`} className={`ml-auto text-[10px] font-black ${state === "READY" ? "text-emerald-700" : "text-rose-600"}`}>{state}</span></div>
            <div className="text-[10px] text-stone-500 font-mono">{e.engine_id} · {e.type}</div>
            <div className="mt-1"><ExecBadge t={e.execution_type} testid={`p11-engine-exec-${e.engine_id}`} /></div>
            <div className="text-[10px] mt-1">Tasks: {e.tasks.join(", ")}</div>
            {state !== "READY" && <div className="text-[10px] text-rose-700 mt-0.5">{(h.reasons || [h.reason || e.contract?.status_reason]).filter(Boolean).join(" · ")}</div>}
          </div>
        );
      })}
    </div>
  );
}

function NewJob({ projectId, engines, onCreated }) {
  const [f, setF] = useState({ task: "text_to_image", prompt: "", requested_engine: "", allow_mock_fallback: false, source_asset_id: "" });
  const submit = async () => {
    try {
      const body = { project_id: projectId, task: f.task, prompt: f.prompt || "untitled", allow_mock_fallback: f.allow_mock_fallback,
        idempotency_key: `ui_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        settings: f.task === "text_to_image" ? { width: 384, height: 384, steps: 8 } : { duration_s: 3 },
        ...(f.requested_engine ? { requested_engine: f.requested_engine } : {}), ...(f.source_asset_id ? { source_asset_id: f.source_asset_id } : {}) };
      const r = await api.post("/aina/creator/p11/jobs", body);
      if (r.data.orchestrator?.downgraded_to_mock) toast.warning(r.data.orchestrator.downgrade_notice);
      else toast.success(`Dispatched to ${r.data.engine}`);
      onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d?.code === "NO_SAFE_ENGINE" ? `No safe engine: ${d.decisions.map((x) => `${x.engine_id}: ${x.reasons.join(",")}`).join(" | ")}` : errMsg(e));
    }
  };
  const sel = "border border-stone-200 rounded-lg px-2 py-1 text-xs";
  return (
    <div className="flex flex-wrap gap-2 items-center" data-testid="p11-new-job">
      <select data-testid="p11-task" className={sel} value={f.task} onChange={(e) => setF({ ...f, task: e.target.value })}>
        {["text_to_image", "image_to_motion", "text_to_video", "image_to_video"].map((t) => <option key={t}>{t}</option>)}
      </select>
      <select data-testid="p11-engine" className={sel} value={f.requested_engine} onChange={(e) => setF({ ...f, requested_engine: e.target.value })}>
        <option value="">Auto (safest)</option>
        {engines.map((e) => <option key={e.engine_id} value={e.engine_id}>{e.engine_id}</option>)}
      </select>
      <input data-testid="p11-prompt" className={`${sel} flex-1 min-w-[160px]`} placeholder="Prompt" value={f.prompt} onChange={(e) => setF({ ...f, prompt: e.target.value })} />
      {f.task === "image_to_motion" && <input data-testid="p11-source-asset" className={sel} placeholder="Source PNG asset_id" value={f.source_asset_id} onChange={(e) => setF({ ...f, source_asset_id: e.target.value })} />}
      <label className="text-[10px] flex items-center gap-1"><input data-testid="p11-allow-mock" type="checkbox" checked={f.allow_mock_fallback} onChange={(e) => setF({ ...f, allow_mock_fallback: e.target.checked })} />Allow mock fallback</label>
      <button data-testid="p11-dispatch" onClick={submit} className="px-3 py-1 rounded-full bg-fuchsia-600 text-white text-[10px] font-black uppercase">Dispatch</button>
    </div>
  );
}

function JobCard({ j, onRetry }) {
  const o = j.orchestrator || {};
  return (
    <details className="rounded-xl border border-stone-200 p-2 text-[11px]" data-testid={`p11-job-${j.job_id}`}>
      <summary className="cursor-pointer flex flex-wrap items-center gap-2">
        <b data-testid={`p11-job-status-${j.job_id}`}>{j.status}</b>
        <span className="font-mono">{j.capability}</span>
        <span>requested: <b>{o.requested_engine || "auto"}</b> → actual: <b data-testid={`p11-job-actual-${j.job_id}`}>{o.selected_engine || "—"}</b></span>
        <ExecBadge t={o.execution_type} testid={`p11-job-exec-${j.job_id}`} />
        <span className="ml-auto">credits {o.cost?.actual_credits ?? 0} (est. {o.cost?.estimated_credits}) · ₹0 charged</span>
      </summary>
      {o.downgrade_notice && <p className="mt-1 text-amber-700 font-semibold">{o.downgrade_notice}</p>}
      {o.error && <p className="mt-1 text-rose-700" data-testid={`p11-job-error-${j.job_id}`}>{o.error.code}: {o.error.message}</p>}
      <div className="mt-2"><b>Selection decisions</b>
        <ul className="list-disc pl-4">{(o.decisions || []).map((d, i) => <li key={i}>{d.engine_id}: {d.eligible ? "eligible" : `rejected — ${d.reasons.join(", ")}`}</li>)}</ul></div>
      <div className="mt-1"><b>Attempts</b>
        <ul className="list-disc pl-4">{(o.attempts || []).map((a, i) => <li key={i}>{a.engine_id}: {a.outcome}{a.error ? ` — ${a.error}` : ""}</li>)}</ul></div>
      <div className="mt-1"><b>Fallback history</b>
        <ul className="list-disc pl-4" data-testid={`p11-job-transitions-${j.job_id}`}>{(o.fallback_transitions || []).map((t, i) => <li key={i}>[{t.phase}] {t.from || "—"} → {t.to || "—"}: {(t.reason || []).join(", ")}</li>)}</ul></div>
      {j.result && (
        <div className="mt-1"><b>Runtime</b> {JSON.stringify(j.result.runtime)} · <b>QC</b> {j.result.qc?.check} {String(j.result.qc?.passed)}
          <div>Outputs: {(j.result.outputs || []).map((x, i) => <a key={i} href={x.mock ? undefined : `${process.env.REACT_APP_BACKEND_URL}${x.url}`} target="_blank" rel="noreferrer" className="underline mr-2">{x.type}{x.mock ? " (mock)" : ""}</a>)}</div>
        </div>
      )}
      {j.status === "FAILED" && <button data-testid={`p11-job-retry-${j.job_id}`} onClick={() => onRetry(j.job_id)} className="mt-2 px-2 py-1 rounded-full bg-stone-900 text-white">Retry</button>}
    </details>
  );
}

export function OrchestratorTab({ projectId }) {
  const [reg, setReg] = useState(null);
  const [health, setHealth] = useState(null);
  const [jobs, setJobs] = useState([]);
  const loadJobs = useCallback(() => api.get(`/aina/creator/p11/jobs?project_id=${projectId}`).then((r) => setJobs(r.data.items)).catch(() => {}), [projectId]);
  useEffect(() => {
    api.get("/aina/creator/p11/engines").then((r) => setReg(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/aina/creator/p11/health").then((r) => setHealth(r.data)).catch(() => {});
    loadJobs();
  }, [loadJobs]);
  useEffect(() => {
    if (!jobs.some((j) => ["QUEUED", "PROCESSING"].includes(j.status))) return undefined;
    const t = setInterval(loadJobs, 2500);
    return () => clearInterval(t);
  }, [jobs, loadJobs]);
  const retry = async (id) => { try { await api.post(`/aina/creator/p11/jobs/${id}/retry`); loadJobs(); } catch (e) { toast.error(errMsg(e)); } };
  if (!reg) return <Loader2 className="w-4 h-4 animate-spin" />;
  return (
    <div className="space-y-3" data-testid="p11-orchestrator">
      <div className="text-[10px] rounded-lg bg-stone-50 p-2" data-testid="p11-policy">
        <b>Policy:</b> {reg.selection_policy}. Paid gates {reg.paid_gates_closed ? "CLOSED" : "MISCONFIGURED"} · external providers disabled · credits are mock (₹0).
        {health && <span> · disk free {health.disk_free_mb} MB · network probes {health.network_probes}</span>}
      </div>
      <EngineList reg={reg} health={health} />
      <NewJob projectId={projectId} engines={reg.engines} onCreated={loadJobs} />
      <div className="space-y-2" data-testid="p11-jobs">
        {jobs.length ? jobs.map((j) => <JobCard key={j.job_id} j={j} onRetry={retry} />) : <p className="text-[11px] text-stone-500" data-testid="p11-jobs-empty">No orchestrated jobs yet.</p>}
      </div>
    </div>
  );
}
